import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { useBrand, useMarket, useTenantPath } from "@/lib/brand";
import { t } from "@/lib/terms";
import Favicon from "@/components/Favicon";

export default function SiteHeader() {
  const brand = useBrand();
  const market = useMarket();
  const biz = brand.business;
  const tenantPath = useTenantPath();
  const letLabel = t("let_verb", market); // "Let" (UK) / "Rent" (US)
  const valuationLabel = market === "us" ? "Home value" : "Free valuation";
  const isTenant = brand.tenant_slug && brand.tenant_slug !== "foundation";
  const homeHref = isTenant ? tenantPath("") : "/";
  const sellHref = isTenant ? tenantPath("sell") : null;
  const letHref = isTenant ? tenantPath("let") : null;
  const propertiesHref = isTenant ? tenantPath("properties") : null;
  const guidesHref = isTenant ? tenantPath("guides") : null;
  const valuationHref = isTenant ? tenantPath("valuation") : null;
  const registerHref = isTenant ? tenantPath("register") : null;
  const aboutHref = isTenant ? tenantPath("about") : null;
  const contactHref = isTenant ? tenantPath("contact") : null;
  const [openMobile, setOpenMobile] = useState(false);

  const closeMobile = () => setOpenMobile(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 w-full bg-background/85 backdrop-blur-md border-b border-border"
    >
      <Favicon />
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-24 md:h-28 flex items-center justify-between gap-6">
        <Link to={homeHref} data-testid="logo-link" className="flex items-center shrink-0" onClick={closeMobile}>
          {/* An agency that has not uploaded a logo gets its own name set as
              a wordmark. It used to get "/agency-logo.png", a real
              customer's mark, on another agency's website. */}
          {brand.assets?.logo_url ? (
            <img
              src={brand.assets.logo_url}
              alt={biz.name}
              className="h-16 md:h-20 w-auto max-w-[140px] md:max-w-[180px] object-contain"
              data-testid="site-logo"
            />
          ) : (
            <span
              className="text-lg md:text-2xl font-semibold tracking-tight text-foreground"
              data-testid="site-logo"
            >
              {biz.name}
            </span>
          )}
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
          {isTenant && (
            <>
              <Link to={sellHref} className="hover:text-primary transition-colors duration-300" data-testid="header-sell">Sell</Link>
              <Link to={letHref} className="hover:text-primary transition-colors duration-300" data-testid="header-let">{letLabel}</Link>
              <Link to={propertiesHref} className="hover:text-primary transition-colors duration-300" data-testid="header-properties">Properties</Link>
              <Link to={guidesHref} className="hover:text-primary transition-colors duration-300" data-testid="header-guides">Guides</Link>
              <Link to={aboutHref} className="hover:text-primary transition-colors duration-300" data-testid="header-about">About</Link>
              <Link to={contactHref} className="hover:text-primary transition-colors duration-300" data-testid="header-contact">Contact</Link>
            </>
          )}
          {biz.phone && (
            <a href={`tel:${(biz.phone_intl || biz.phone).replace(/\s/g, "")}`} className="flex items-center gap-2 hover:text-primary transition-colors duration-300" data-testid="header-phone">
              <Phone className="w-4 h-4" /> {biz.phone}
            </a>
          )}
        </div>

        {/* Mobile hamburger */}
        {isTenant && (
          <button
            className="md:hidden p-2 text-primary"
            onClick={() => setOpenMobile((o) => !o)}
            data-testid="header-mobile-toggle"
            aria-label="Toggle menu"
          >
            {openMobile ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        )}
      </div>

      {/* Mobile drawer */}
      {isTenant && openMobile && (
        <div className="md:hidden border-t border-border bg-background" data-testid="header-mobile-drawer">
          <nav className="px-6 py-4 flex flex-col text-sm">
            <MobileLink to={sellHref} testid="header-sell-mobile" onClick={closeMobile}>Sell</MobileLink>
            <MobileLink to={letHref} testid="header-let-mobile" onClick={closeMobile}>{letLabel}</MobileLink>
            <MobileLink to={propertiesHref} testid="header-properties-mobile" onClick={closeMobile}>Properties</MobileLink>
            <MobileLink to={guidesHref} testid="header-guides-mobile" onClick={closeMobile}>Guides</MobileLink>
            <MobileLink to={valuationHref} testid="header-valuation-mobile" onClick={closeMobile}>{valuationLabel}</MobileLink>
            <MobileLink to={aboutHref} testid="header-about-mobile" onClick={closeMobile}>About</MobileLink>
            <MobileLink to={contactHref} testid="header-contact-mobile" onClick={closeMobile}>Contact</MobileLink>
            <MobileLink to={registerHref} testid="header-register-mobile" onClick={closeMobile}>Register</MobileLink>
            {biz.phone && (
              <a
                href={`tel:${(biz.phone_intl || biz.phone).replace(/\s/g, "")}`}
                className="py-3 flex items-center gap-2 text-primary"
                data-testid="header-phone-mobile"
                onClick={closeMobile}
              >
                <Phone className="w-4 h-4" /> {biz.phone}
              </a>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}

function MobileLink({ to, children, testid, onClick }) {
  return (
    <Link to={to} data-testid={testid} onClick={onClick} className="py-3 border-b border-border last:border-0 text-primary hover:text-accent">
      {children}
    </Link>
  );
}
